import { ReactElement } from "react";
import CabinetPage from "../../components/Common/CabinetPage";
import { Box, Grid, Typography } from "@mui/material";
import { CabButton } from "@CabComponents/CabButton";
import { CabIcon } from "@CabComponents/CabIcon";
import { Leader } from "../../store";
import { CategoryItem } from ".";
import ExecutiveProfileHeader from "./ExecutiveProfileHeader";
import NotesWidget from "./NotesWidget";
import { LeaderCard } from "../../components/InfoCard/LeaderCard/LeaderCard";
import LeaderMeetingsWidget from "./LeaderMeetingsWidget";
import LeaderRecurringMeetingsWidget from "./LeaderRecurringMeetingsWidget";
import { IoShareSocial, IoCreateOutline } from "react-icons/io5";
import { CabTooltip } from "@CabComponents/CabTooltip";


export interface ExecutiveProfileProps {
  leader: Leader;
  categories: CategoryItem[];
  customCategories?: CategoryItem[];
  onClickEdit: () => void;
  onClickShare: () => void;
  onClickAddCategory: () => void;
  notesLocked: boolean;
  meetingsLoaded: boolean;
  categoriesLoaded: boolean;
}

export const ExecutiveProfile = ({
  leader, categories, customCategories, onClickEdit, onClickShare, onClickAddCategory, notesLocked, meetingsLoaded,
  categoriesLoaded
}: ExecutiveProfileProps): ReactElement => {

  const canShare = !leader.is_shared || leader.permissions.is_admin;
  const noSharingTooltip = "You must be the owner or an admin for this profile in order to share it with someone else.";

  return (
    <CabinetPage
      pageName={'ExecutiveProfile'}
      noBorder
      headerMinHeight={35}
      headerContent={ 
        <Box
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            display: { xs: 'flex', md: 'none' }
          }}>
          <Typography variant="h1">{`${leader.first_name} ${leader.last_name}`}</Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2
            }}>
            <CabTooltip wrapWithSpan title={canShare ? "" : noSharingTooltip} spanSx={{alignSelf: "center"}}>
              <CabIcon alt='Share' Icon={IoShareSocial} onClick={onClickShare} disabled={!canShare}/>
            </CabTooltip>
            <CabIcon alt='Edit' Icon={IoCreateOutline} onClick={onClickEdit} />
          </Box>
        </Box>
      }>
      <Box
        sx={{
          marginLeft: {xs: 2, sm: 5},
          marginRight: {xs: 2, sm: 5},
          height: '100%'
        }}>
        <Grid
          container
          spacing={4}
          sx={{
            height: '100%',
            rowGap: 4
          }}>
          <Grid size={{ xs: 12, sm: 12, md: 7 }} container spacing={4} sx={{
            height: '100%'
          }}>
            <Grid
              size={12}
              sx={{
                height: '16%',
                minHeight: 170,
                display: { xs: 'none', md: 'flex' }
              }}>
              <ExecutiveProfileHeader leader={leader} />
            </Grid>
            <Grid
              size={12}
              sx={{
                height: '16%',
                minHeight: 140,
                display: { xs: 'flex', md: 'none' }
              }}>
              <ExecutiveProfileHeader leader={leader} noName />
            </Grid>
            <Grid size={12} sx={{
              height: '42%'
            }}>
              <LeaderMeetingsWidget meetingsLoaded={meetingsLoaded} leaderId={leader.id} />
            </Grid>
            <Grid size={12} sx={{
              height: '42%'
            }}>
              <LeaderRecurringMeetingsWidget meetingsLoaded={meetingsLoaded} leaderId={leader.id} />
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 5 }} container spacing={4} sx={{
            height: '100%'
          }}>
            <Grid
              size={12}
              sx={{
                height: '16%',
                minHeight: 170,
                display: { xs: 'none', md: 'flex' }
              }}>
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  gap: 1,
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end'
                }}>
                <CabTooltip wrapWithSpan title={canShare ? "" : noSharingTooltip}>
                  <CabButton onClick={onClickShare} disabled={!canShare}
                    icon={<CabIcon alt='Share' Icon={IoShareSocial} />}
                  >
                    Share
                  </CabButton>
                </CabTooltip>
                <CabTooltip wrapWithSpan title={canShare ? "" : noSharingTooltip}>
                  <CabButton buttonType='tertiary' onClick={onClickEdit} disabled={!canShare}
                    icon={<CabIcon alt='Edit' Icon={IoCreateOutline}/>}
                  >
                    Edit
                  </CabButton>
                </CabTooltip>
              </Box>
            </Grid>
            <Grid size={12} sx={{
              height: {xs: '50%', md:'42%'}
            }}>
              <NotesWidget
                categories={[...categories, ...(customCategories || [])]}
                loading={!categoriesLoaded}
                onClickAddCategory={!leader.is_shared || leader.permissions.is_admin ? onClickAddCategory : undefined}
                locked={notesLocked}
              />
            </Grid>
            <Grid size={12} sx={{
              height: {xs: '50%', md:'42%'}
            }}>
              <LeaderCard currentLeader={leader}/>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </CabinetPage>
  );
};

export default ExecutiveProfile;
